import * as React from "react"

import { cva } from "../cva.ts"
import { useLayoutEffect, useUuid } from "../hooks.ts"
import { withClass } from "../to-wrapped-children.ts"

export const Field = ({
  name,
  validationMessage: validationMessageProp,
  required: defaultRequired,
  ...props
}: Field.Props) => {
  const [validityState, setValidityState] = React.useState<Field.Validity>(
    validationMessageProp ? "customError" : "valid",
  )

  const [validationMessage, setValidationMessage] = React.useState(validationMessageProp ?? "")
  const [required, setRequired] = React.useState(Boolean(defaultRequired))

  const field: Field.Refs = {
    name,
    section: React.useRef<HTMLSpanElement>(null),
    control: React.useRef<Field.Control>(null),
    label: React.useRef<HTMLLabelElement>(null),
    validityState,
    setValidityState,
    validationMessage,
    setValidationMessage,
    required,
    setRequired,
    uuid: useUuid(),
  }

  React.useImperativeHandle(props.ref, () => field.section.current!)

  useLayoutEffect(() => {
    setValidityState(validationMessageProp ? "customError" : "valid")
    setValidationMessage(validationMessageProp ?? "")
  }, [validationMessageProp])

  return (
    <FieldRefs value={field}>
      <span data-ui="Field" ref={field.section} {...withClass(props, classForField)} />
    </FieldRefs>
  )
}

export namespace Field {
  export interface Control extends HTMLElement {
    validationMessage: string
    validity: ValidityState

    setCustomValidity(message: string): void
  }

  export type Props = React.ComponentProps<"span"> & {
    name: string
    required?: boolean
    validationMessage?: string
  }

  export type Refs = {
    /** Field name given to form data. */
    name: string

    /** Returns IDs for related elements unique to the field. */
    uuid: useUuid.ReturnType

    /** Ref to the DOM element representing the field section. */
    section: React.RefObject<HTMLSpanElement | null>

    /** Ref to the DOM element representing the field label. */
    label: React.RefObject<HTMLLabelElement | null>

    /** Ref to the DOM element representing the field control (input, select, etc.). */
    control: React.RefObject<Control | null>

    /** Represents the current validity state of the field. */
    validityState: Validity

    /** Updates the validity state of the field. */
    setValidityState: React.Dispatch<React.SetStateAction<Validity>>

    /** The current validation message associated with the field. */
    validationMessage: string

    /** Updates the validation message for the field. */
    setValidationMessage: React.Dispatch<React.SetStateAction<string>>

    /** Indicates the user must specify a value for the input before submission. */
    required: boolean

    /** Sets whether the user must specify a value for the input before submission. */
    setRequired: React.Dispatch<React.SetStateAction<boolean>>
  }

  /** A field validity state. */
  export type Validity =
    | "badInput"
    | "customError"
    | "patternMismatch"
    | "rangeOverflow"
    | "rangeUnderflow"
    | "stepMismatch"
    | "tooLong"
    | "tooShort"
    | "typeMismatch"
    | "valueMissing"
    | "valid"

  /** Function for validating a field, returning a custom validation message. */
  export type Validate<T extends Control> = (control: T) => string | void
}

export const classForField = cva(["flex flex-col gap-2"])

// #region Use Field Refs Hook

export const useFieldRefs = () => React.use(FieldRefs)

const FieldRefs = React.createContext<Field.Refs>({
  name: "field",
  uuid: (...parts: string[]) => parts.map((part) => part + ":").join(" "),
  section: { current: null },
  label: { current: null },
  control: { current: null },
  validityState: "valid",
  setValidityState() {},
  validationMessage: "",
  setValidationMessage() {},
  required: false,
  setRequired() {},
})

// #endregion
